import type { RenderCallbackContent } from '../types/cell-content'

/**
 * Angular core types - defined locally to avoid requiring @angular/core as a dependency
 */
interface Type<T> extends Function {
  new (...args: any[]): T
}

interface ComponentRef<T> {
  instance: T
  setInput: (name: string, value: unknown) => void
  changeDetectorRef: { detectChanges: () => void }
  destroy: () => void
}

interface EnvironmentInjector {
  get: (token: any) => any
}

/**
 * Options for creating an Angular cell
 */
export interface AngularCellOptions {
  /**
   * Angular component class to render
   * Uses Type<any> to support different Angular component types
   */
  component: Type<any>

  /**
   * Input bindings to pass to the component
   * These are set using Angular's setInput() method (Angular 14+)
   */
  inputs?: Record<string, any>

  /**
   * Output event handlers to subscribe to
   * Each output is expected to be an EventEmitter that will be subscribed to
   * @example
   * {
   *   click: (e) => console.log('clicked', e),
   *   change: (value) => console.log('changed', value)
   * }
   */
  outputs?: Record<string, (...args: any[]) => void>

  /**
   * Content to project into the component (ng-content)
   * Note: Dynamic content projection is limited in Angular
   */
  content?: string

  /**
   * Optional EnvironmentInjector for dependency injection
   * If not provided, component will be created without DI context
   */
  environmentInjector?: EnvironmentInjector
}

/**
 * Type for Angular's createComponent function
 * Made flexible to support different Angular versions
 */
type CreateComponentFn = (
  component: Type<any>,
  options: Record<string, any>,
) => ComponentRef<any>

/**
 * Create a datatable cell that renders an Angular component using a factory function.
 *
 * This helper manages the Angular component lifecycle, creating and destroying
 * the component when the cell is added/removed from the table.
 *
 * Note: This helper requires Angular 14+ for the `createComponent` function
 * and `setInput` method support.
 *
 * @param createComponentFn - Angular's createComponent function from @angular/core
 * @param options - Angular cell options
 * @returns Render callback content for the datatable
 *
 * @example
 * ```typescript
 * import { createComponent, EnvironmentInjector, inject } from '@angular/core'
 * import { createAngularCellWithFactory } from 'dap-design-system/helpers/angular'
 * import { StatusBadgeComponent } from './status-badge.component'
 *
 * // Inside a component class
 * private readonly injector = inject(EnvironmentInjector)
 *
 * columns = [
 *   {
 *     id: 'status',
 *     header: 'Status',
 *     cell: (info) => createAngularCellWithFactory(createComponent, {
 *       component: StatusBadgeComponent,
 *       inputs: {
 *         status: info.row.original.status,
 *         variant: 'success'
 *       },
 *       outputs: {
 *         click: () => console.log('Badge clicked')
 *       },
 *       environmentInjector: this.injector
 *     })
 *   }
 * ]
 * ```
 */
export function createAngularCellWithFactory(
  createComponentFn: CreateComponentFn,
  options: AngularCellOptions,
): RenderCallbackContent {
  const { component, inputs, outputs, content, environmentInjector } = options

  // Track the component ref and subscriptions
  let componentRef: ComponentRef<any> | null = null
  const subscriptions: Array<{ unsubscribe: () => void }> = []

  return {
    render: (container: HTMLElement) => {
      try {
        // Handle content projection if provided
        if (content) {
          container.innerHTML = content
        }

        // Create the Angular component
        // Use a generic options object to support different Angular versions
        const createOptions: Record<string, any> = {
          hostElement: container,
        }

        if (environmentInjector) {
          createOptions.environmentInjector = environmentInjector
        }

        componentRef = createComponentFn(component, createOptions)

        // Set inputs using Angular's setInput method (Angular 14+)
        if (inputs && componentRef) {
          Object.entries(inputs).forEach(([key, value]) => {
            componentRef!.setInput(key, value)
          })
        }

        // Subscribe to outputs (EventEmitters)
        if (outputs && componentRef) {
          Object.entries(outputs).forEach(([outputName, handler]) => {
            const outputEmitter = componentRef!.instance[outputName]
            if (
              outputEmitter &&
              typeof outputEmitter.subscribe === 'function'
            ) {
              const subscription = outputEmitter.subscribe(handler)
              subscriptions.push(subscription)
            }
          })
        }

        // Trigger change detection
        if (componentRef) {
          componentRef.changeDetectorRef.detectChanges()
        }
      } catch (error) {
        console.error(
          'Error rendering Angular component in datatable cell:',
          error,
        )
        container.textContent = 'Error rendering cell'
      }
    },

    cleanup: () => {
      // Unsubscribe from all outputs
      subscriptions.forEach(subscription => {
        try {
          subscription.unsubscribe()
        } catch (error) {
          console.error(
            'Error unsubscribing from Angular output in datatable cell:',
            error,
          )
        }
      })
      subscriptions.length = 0

      // Destroy the component
      if (componentRef !== null) {
        try {
          componentRef.destroy()
        } catch (error) {
          console.error(
            'Error destroying Angular component in datatable cell:',
            error,
          )
        } finally {
          componentRef = null
        }
      }
    },
  }
}

/**
 * @deprecated Use createAngularCellWithFactory instead.
 * This alias is provided for backwards compatibility.
 */
export const createAngularCell = createAngularCellWithFactory

/**
 * Type helper for creating type-safe cell renderers
 *
 * @example
 * ```typescript
 * import type { AngularCellRenderer } from 'dap-design-system/helpers/angular'
 *
 * interface User {
 *   id: number
 *   status: string
 * }
 *
 * const statusCell: AngularCellRenderer<User> = (row) => createAngularCellWithFactory(createComponent, {
 *   component: StatusBadgeComponent,
 *   inputs: { status: row.status }
 * })
 * ```
 */
export type AngularCellRenderer<TData = any> = (
  row: TData,
) => RenderCallbackContent
